import { useState, useCallback } from "react";
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
} from "firebase/auth";

/**
 * Custom hook encapsulating password change logic with validation.
 *
 * @param {object} params
 * @param {object} params.currentUser - Firebase auth user.
 * @param {string} params.language - "en" | "zh".
 */
export function usePasswordChange({ currentUser, language }) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState({
        text: "",
        type: "",
    });
    const [showPasswordSection, setShowPasswordSection] = useState(false);

    const togglePasswordSection = useCallback(() => {
        setShowPasswordSection((prev) => !prev);
        setPasswordMessage({ text: "", type: "" });
    }, []);

    const validatePassword = useCallback(() => {
        if (newPassword.length < 8) {
            const msg =
                language === "zh"
                    ? "新密碼必須至少 8 個字元。"
                    : "New password must be at least 8 characters.";
            setPasswordMessage({ text: msg, type: "error" });
            return false;
        }
        if (!/[A-Z]/.test(newPassword)) {
            const msg =
                language === "zh"
                    ? "新密碼必須包含至少一個大寫字母。"
                    : "New password must contain at least one uppercase letter.";
            setPasswordMessage({ text: msg, type: "error" });
            return false;
        }
        if (!/[0-9]/.test(newPassword)) {
            const msg =
                language === "zh"
                    ? "新密碼必須包含至少一個數字。"
                    : "New password must contain at least one number.";
            setPasswordMessage({ text: msg, type: "error" });
            return false;
        }
        if (newPassword !== confirmPassword) {
            const msg =
                language === "zh"
                    ? "新密碼和確認密碼不一致。"
                    : "Passwords do not match.";
            setPasswordMessage({ text: msg, type: "error" });
            return false;
        }
        return true;
    }, [newPassword, confirmPassword, language]);

    const handlePasswordChange = useCallback(
        async (e) => {
            e.preventDefault();
            if (!validatePassword()) return;

            setPasswordLoading(true);
            setPasswordMessage({ text: "", type: "" });

            try {
                const credential = EmailAuthProvider.credential(
                    currentUser.email,
                    currentPassword,
                );
                await reauthenticateWithCredential(currentUser, credential);
                await updatePassword(currentUser, newPassword);

                const msg =
                    language === "zh"
                        ? "密碼更新成功！"
                        : "Password updated successfully!";
                setPasswordMessage({ text: msg, type: "success" });
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
            } catch (err) {
                let errorMsg;
                if (
                    err.code === "auth/wrong-password" ||
                    err.code === "auth/invalid-credential"
                ) {
                    errorMsg =
                        language === "zh"
                            ? "目前的密碼不正確。"
                            : "Current password is incorrect.";
                } else if (err.code === "auth/too-many-requests") {
                    errorMsg =
                        language === "zh"
                            ? "嘗試次數過多，請稍後再試。"
                            : "Too many attempts. Please try again later.";
                } else {
                    errorMsg =
                        language === "zh"
                            ? `更新密碼時發生錯誤：${err.message}`
                            : `Error updating password: ${err.message}`;
                }
                setPasswordMessage({ text: errorMsg, type: "error" });
            } finally {
                setPasswordLoading(false);
            }
        },
        [currentUser, currentPassword, newPassword, validatePassword, language],
    );

    return {
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmPassword,
        setConfirmPassword,
        passwordLoading,
        passwordMessage,
        showPasswordSection,
        togglePasswordSection,
        handlePasswordChange,
    };
}
